import React from 'react';
import { Button, Typography } from 'antd';
import { VirtualCollection } from 'flare-ui-primitives';
import {
  FileTextOutlined,
  FolderOutlined,
  MessageOutlined,
  PlusOutlined,
  PushpinFilled,
  PushpinOutlined,
  ReloadOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { getProjectId } from '../../session/sessionIdentity.js';
import ProjectManagementSearch from './ProjectManagementSearch.jsx';

const { Text, Title } = Typography;
const CARD_GAP = 16;
const CARD_MIN_WIDTH = 240;
const CREATE_PROJECT_ITEM = Object.freeze({ kind: 'create_project' });
const explicitCount = (value) => (
  Number.isFinite(Number(value)) && Number(value) >= 0 ? Number(value) : null
);
const projectName = (project = {}) => (
  String(project.name ?? project.project_name ?? '').trim() || '未命名项目'
);

function ProjectMetric({ icon, label, value, themeTokens }) {
  const count = explicitCount(value);
  if (count === null) return null;
  return (
    <span aria-label={`${count} ${label}`} className="flare-project-management-metric" style={{ color: themeTokens.textMuted }}>
      {icon}
      <span aria-hidden="true">{count}</span>
      <span>{label}</span>
    </span>
  );
}

function ProjectCard({ project, onPinProject, onProjectSelect, themeTokens }) {
  const name = projectName(project);
  const pinned = project.user_pinned === true;
  const pinLabel = pinned ? '取消置顶项目' : '置顶项目';
  const [pinState, setPinState] = React.useState({ pending: false, error: false });
  const mounted = React.useRef(false);
  React.useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const handlePin = async () => {
    setPinState({ pending: true, error: false });
    try {
      await onPinProject?.(project);
    } catch {
      if (mounted.current) setPinState({ pending: false, error: true });
      return;
    }
    if (mounted.current) setPinState({ pending: false, error: false });
  };
  return (
    <div
      className="flare-project-management-card"
      style={{ background: themeTokens.panelBg, borderColor: themeTokens.divider, color: themeTokens.textPrimary }}
    >
      <button
        aria-label={`打开项目 ${name}`}
        className="flare-project-management-card-open"
        onClick={() => onProjectSelect?.(project)}
        type="button"
      >
        <span className="flare-project-management-card-icon" style={{ color: themeTokens.textSecondary }}>
          <FolderOutlined />
        </span>
        <RightOutlined className="flare-project-management-card-arrow" aria-hidden="true" />
        <Text className="flare-project-management-card-title" ellipsis strong style={{ color: themeTokens.textPrimary }}>
          {name}
        </Text>
        <span className="flare-project-management-card-metrics">
          <ProjectMetric icon={<MessageOutlined aria-hidden="true" />} label="会话" value={project.session_count} themeTokens={themeTokens} />
          <ProjectMetric icon={<FileTextOutlined aria-hidden="true" />} label="产物" value={project.artifact_count} themeTokens={themeTokens} />
        </span>
      </button>
      {typeof onPinProject === 'function' ? (
        <Button
          aria-label={pinLabel}
          aria-pressed={pinned}
          className="flare-project-management-pin"
          disabled={pinState.pending}
          icon={pinned ? <PushpinFilled /> : <PushpinOutlined />}
          loading={pinState.pending}
          onClick={handlePin}
          size="small"
          style={{ color: themeTokens.textSecondary }}
          title={pinLabel}
          type="text"
        />
      ) : null}
      {pinState.error ? <Text className="flare-project-management-card-error" role="alert" type="danger">
        置顶操作失败，请重试
      </Text> : null}
    </div>
  );
}

/** Render the canonical Project directory without exposing Session actions. */
export default function ProjectManagementPane(props) {
  const {
    contentMaxWidth,
    collectionScopeKey,
    createProjectDisabled,
    createProjectLabel,
    error,
    onCreateProject,
    onLoadMoreProjects,
    onPinProject,
    onProjectSelect,
    onRetry,
    projectSearch,
    projectHasMore,
    projectContinuationKey,
    projectItems,
    projectLoadingMore,
    projectManagementLabel,
    themeTokens,
  } = {
    createProjectDisabled: false,
    createProjectLabel: '创建项目',
    projectHasMore: false,
    projectItems: [],
    projectLoadingMore: false,
    projectManagementLabel: '项目空间',
    themeTokens: {},
    ...props,
  };
  const originalCollection = { items: projectItems, error, hasMore: projectHasMore,
    loadingMore: projectLoadingMore, onLoadMore: onLoadMoreProjects, onRetry,
    window: { nextCursor: projectContinuationKey } };
  const collection = projectSearch?.collection('projects', originalCollection) ?? originalCollection;
  const canonicalProjectItems = React.useMemo(
    () => (Array.isArray(collection.items) ? collection.items : []),
    [collection.items],
  );
  const collectionItems = React.useMemo(
    () => [CREATE_PROJECT_ITEM, ...canonicalProjectItems],
    [canonicalProjectItems],
  );
  const resolvedCreateProjectLabel = createProjectLabel || '创建项目';
  const resolvedProjectManagementLabel = projectManagementLabel || '项目空间';
  const [scrollElement, setScrollElement] = React.useState(null);
  const [columnCount, setColumnCount] = React.useState(1);
  React.useLayoutEffect(() => {
    if (!scrollElement) return undefined;
    const measure = () => {
      const width = scrollElement.clientWidth;
      setColumnCount(Math.max(1, Math.floor((width + CARD_GAP) / (CARD_MIN_WIDTH + CARD_GAP))));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(scrollElement);
    return () => observer.disconnect();
  }, [scrollElement]);
  return (
    <main
      className="flare-project-management"
      style={{ color: themeTokens.textPrimary }}
    >
      <div className="flare-project-management-content" style={{ maxWidth: contentMaxWidth }}>
        <header className="flare-project-management-header">
          <Title level={2} style={{ color: themeTokens.textPrimary, margin: 0 }}>{resolvedProjectManagementLabel}</Title>
        </header>
        <ProjectManagementSearch projectSearch={projectSearch} />
        {collection.error ? (
          <div className="flare-project-management-error" role="alert" style={{ borderColor: themeTokens.divider }}>
            <Text style={{ color: themeTokens.textSecondary }}>项目列表暂时不可用</Text>
            <Button icon={<ReloadOutlined />} onClick={collection.onRetry} size="small" type="text">重试</Button>
          </div>
        ) : null}
        <section
          aria-label={resolvedProjectManagementLabel}
          className="flare-project-management-grid-scroll"
          ref={setScrollElement}
        >
          <VirtualCollection
            ariaLabel={resolvedProjectManagementLabel}
            className="flare-project-management-grid"
            continuation={{
              error: collection.error ?? null,
              hasMore: collection.hasMore,
              key: String(collection.window.nextCursor ?? ''),
              loading: collection.loadingMore,
            }}
            getItemKey={item => item === CREATE_PROJECT_ITEM
              ? 'create-project'
              : (getProjectId(item) || projectName(item))}
            items={collectionItems}
            layout={{ columns: columnCount, endThreshold: 120, estimateSize: 184, gap: CARD_GAP, overscan: 2 }}
            onLoadMore={collection.onLoadMore}
            renderItem={item => item === CREATE_PROJECT_ITEM ? (
              <button
                aria-label={resolvedCreateProjectLabel}
                className="flare-project-management-create-card"
                disabled={createProjectDisabled}
                onClick={() => onCreateProject?.()}
                style={{ borderColor: themeTokens.textMuted, color: themeTokens.textPrimary }}
                type="button"
              >
                <PlusOutlined aria-hidden="true" />
                <span>{resolvedCreateProjectLabel}</span>
              </button>
            ) : (
              <ProjectCard
                onPinProject={onPinProject}
                onProjectSelect={onProjectSelect}
                project={item}
                themeTokens={themeTokens}
              />
            )}
            scopeKey={`project-directory:${collectionScopeKey}`}
            scrollElement={scrollElement}
          />
        </section>
      </div>
    </main>
  );
}
